import React, { useState, useEffect } from 'react';
import ConfirmDialog from './ui/ConfirmDialog';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import { useExport } from './header/exporters';
import { toggleCardFilter } from '../utils/cardFilter';
import { useMoney } from '../hooks/useMoney';
import { getAppLocale } from '../utils/locale';
import { recordLabel } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import idColumn from './ui/idColumn';
import DataTable, { Column } from './DataTable';
import Icon from './Icon';
import { MetricCard, ProgressMetricCard, MetricTone } from './MetricCards';
import { apiGet, apiPost, apiPut, apiDelete , planningPath } from '../services/PlanningApiClient';
import { getTeamMembersCache } from '../services/teamMembersCache';
import { useStoredEvents } from '../hooks/useStoredEvents';
import RichTextEditor from './ui/RichTextEditor';
import SearchableSelect from './ui/SearchableSelect';
import StatusCard from './ui/StatusCard';
import { useNotifications } from './NotificationProvider';
import LoadErrorBox from './ui/LoadErrorBox';
import '../styles/components/StationeryTracker.css';

type ItemStatus =
  | 'not-started'
  | 'design-in-progress'
  | 'design-approved'
  | 'sent-to-print'
  | 'printed'
  | 'distributed';

type ItemCategory =
  | 'invitation'
  | 'programme'
  | 'signage'
  | 'favour'
  | 'packaging'
  | 'digital';

interface StationeryItem {
  id: string;
  name: string;
  category: ItemCategory | string;
  quantityNeeded: number;
  quantityPrinted: number;
  designer: string;
  // Loose reference to a real vendors.vendor_id, when the designer/print
  // vendor was picked from the vendor directory rather than typed as a
  // one-off name — see ParsedVendor in data/vendorData.ts.
  designerVendorId?: string;
  designFileLink: string;
  printVendor: string;
  printVendorVendorId?: string;
  costPerUnit: number;
  status: ItemStatus;
  dueDate: string;
  notes: string;
  eventId?: string;    // links to StoredEvent.id
  archived?: boolean;
}

// ── In-memory cache (no localStorage for app data) ──
let stationeryCache: StationeryItem[] | null = null;

const generateId = () => `st-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const ITEM_STATUSES: ItemStatus[] = [
  'not-started',
  'design-in-progress',
  'design-approved',
  'sent-to-print',
  'printed',
  'distributed',
];

const STATUS_LABELS: Record<ItemStatus, string> = {
  'not-started': 'Not Started',
  'design-in-progress': 'Design In Progress',
  'design-approved': 'Design Approved',
  'sent-to-print': 'Sent to Print',
  'printed': 'Printed',
  'distributed': 'Distributed',
};

const STATUS_BADGE: Record<ItemStatus, 'inactive' | 'info' | 'active' | 'warning' | 'completed'> = {
  'not-started':       'inactive',
  'design-in-progress': 'info',
  'design-approved':   'active',
  'sent-to-print':     'warning',
  'printed':           'info',
  'distributed':       'completed',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'ds-badge-warning',
  active: 'ds-badge-success',
  completed: 'ds-badge-success',
  inactive: 'ds-badge-neutral',
  info: 'ds-badge-info',
  warning: 'ds-badge-warning',
};

const STATUS_SELECT_CLASS: Record<ItemStatus, string> = {
  'not-started': 's-neutral',
  'design-in-progress': 's-info',
  'design-approved': 's-purple',
  'sent-to-print': 's-warning',
  'printed': 's-teal',
  'distributed': 's-success',
};

const ITEM_CATEGORIES: ItemCategory[] = [
  'invitation', 'programme', 'signage', 'favour', 'packaging', 'digital',
];

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  invitation: 'Invitation',
  programme: 'Programme',
  signage: 'Signage',
  favour: 'Favour',
  packaging: 'Packaging',
  digital: 'Digital',
};

const CATEGORY_COLORS: Record<string, string> = {
  invitation: '#7c3aed',
  programme: '#1d4ed8',
  signage: '#0f766e',
  favour: '#c2410c',
  packaging: '#b45309',
  digital: '#0369a1',
};

const KANBAN_COLS: ItemStatus[] = ITEM_STATUSES;

function getItemPriority(item: StationeryItem): 'high' | 'neutral' {
  if (!item.dueDate) return 'neutral';
  if (item.status === 'printed' || item.status === 'distributed') return 'neutral';
  return new Date(item.dueDate) < new Date() ? 'high' : 'neutral';
}

function loadItems(): StationeryItem[] {
  return stationeryCache !== null ? stationeryCache : [];
}

/** Public accessor — lets FormPage fall back to the in-memory cache instead of localStorage */
export function loadStationeryItems(): StationeryItem[] {
  return loadItems();
}

function saveItems(items: StationeryItem[]) {
  stationeryCache = items;
}

const blankForm = (): Omit<StationeryItem, 'id'> => ({
  name: '',
  category: 'invitation',
  quantityNeeded: 0,
  quantityPrinted: 0,
  designer: '',
  designFileLink: '',
  printVendor: '',
  costPerUnit: 0,
  status: 'not-started',
  dueDate: '',
  notes: '',
  eventId: '',
});

// syncStationeryBudget is intentionally a no-op: budget is owned by budgetStorage.ts
// and is refreshed from the API on load. Stationery cost sync to budget happens
// through the API write path (apiPut on item update).
function syncStationeryBudget(_totalCost: number) {
  // no-op — budget data is not stored in localStorage
}

const STATUS_CARDS: { status: ItemStatus; label: string; tone: MetricTone; icon: string }[] = [
  { status: 'not-started', label: 'Not Started', tone: 'orange', icon: 'list' },
  { status: 'design-in-progress', label: 'Design In Progress', tone: 'blue', icon: 'edit' },
  { status: 'design-approved', label: 'Design Approved', tone: 'teal', icon: 'check' },
  { status: 'sent-to-print', label: 'Sent to Print', tone: 'red', icon: 'document' },
  { status: 'printed', label: 'Printed', tone: 'pink', icon: 'document' },
  { status: 'distributed', label: 'Distributed', tone: 'green', icon: 'check' },
];

// ── Main Component ────────────────────────────────────────────────────────────

interface StationeryTrackerProps {
  activeCategory?: string;
  onCategoryChange?: (cat: string) => void;
  /** Status card currently used as the filter ('All' = none). */
  filterStatus?: string;
  onFilterStatusChange?: (status: string) => void;
  /** The Archived switch (it lives in Header): off shows active items, on shows archived ones. */
  showArchived?: boolean;
  viewMode?: 'list' | 'kanban';
  onViewModeChange?: (view: 'list' | 'kanban') => void;
  triggerAddItem?: boolean;
  onAddItemHandled?: () => void;
  onItemClick?: (item: StationeryItem) => void;
}

const StationeryTracker: React.FC<StationeryTrackerProps> = ({
  activeCategory: externalCategory,
  onCategoryChange,
  filterStatus: externalStatus,
  onFilterStatusChange,
  showArchived = false,
  viewMode: externalViewMode,
  onViewModeChange,
  triggerAddItem,
  onAddItemHandled,
  onItemClick,
}) => {
  // Re-render when the record numbers (PO-130 instead of PO-90F0D6B4) load.
  useRecordRefs();
  const money = useMoney();
  const [items, setItems] = useState<StationeryItem[]>(() => loadItems());
  const [internalCategory, setInternalCategory] = useState('All');
  const [internalStatus, setInternalStatus] = useState<ItemStatus | 'All'>('All');
  const [internalViewMode, setInternalViewMode] = useState<'list' | 'kanban'>('list');
  const activeCategory = externalCategory ?? internalCategory;
  const setActiveCategory = (v: string) => { setInternalCategory(v); onCategoryChange?.(v); };
  const filterStatus = (externalStatus ?? internalStatus) as ItemStatus | 'All';
  const setFilterStatus = (v: ItemStatus | 'All') => { setInternalStatus(v); onFilterStatusChange?.(v); };
  // The status cards are the filter: click one to filter, click it again to clear.
  const toggleStatusFilter = (status: ItemStatus) => setFilterStatus(toggleCardFilter<ItemStatus | 'All'>(filterStatus, status, 'All'));
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = (v: 'list' | 'kanban') => { setInternalViewMode(v); onViewModeChange?.(v); };
  const [teamMembers, setTeamMembers] = useState<{ id: string; name: string }[]>([]);
  const events = useStoredEvents();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<StationeryItem | null>(null);
  const [form, setForm] = useState<Omit<StationeryItem, 'id'>>(blankForm());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const { showSuccess } = useNotifications();

  useEffect(() => {
    const members = getTeamMembersCache();
    setTeamMembers(
      members
        .filter(m => m.isActive !== false)
        .map(m => ({ id: m.id, name: m.name }))
    );
    setIsLoading(true);
    setLoadError(null);
    refreshStationeryFromApi().then(apiItems => {
      if (apiItems.length > 0) setItems(apiItems);
      setIsLoading(false);
    }).catch((err: unknown) => {
      setLoadError(err ?? new Error('load failed'));
      setIsLoading(false);
    });
  }, [loadAttempt]);

  useEffect(() => {
    saveItems(items);
    const totalCost = items.reduce((sum, it) => sum + it.costPerUnit * it.quantityNeeded, 0);
    syncStationeryBudget(totalCost);
  }, [items]);

  const openAdd = () => {
    setEditingItem(null);
    setForm(blankForm());
    setShowModal(true);
  };

  useEffect(() => {
    if (triggerAddItem) {
      openAdd();
      onAddItemHandled?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAddItem]);

  // Listen for header Save/Delete events when inline form is open
  useEffect(() => {
    if (!showModal) return;
    const onSave = () => handleSave();
    const onDelete = () => {
      if (editingItem) {
        if (editingItem.archived) {
          handlePermanentDelete(editingItem.id);
        } else {
          handleArchive(editingItem.id);
          closeModal();
        }
      }
    };
    window.addEventListener('form-save', onSave);
    window.addEventListener('form-delete', onDelete);
    return () => {
      window.removeEventListener('form-save', onSave);
      window.removeEventListener('form-delete', onDelete);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, editingItem, form]);

  const openEdit = (item: StationeryItem) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      category: item.category,
      quantityNeeded: item.quantityNeeded,
      quantityPrinted: item.quantityPrinted,
      designer: item.designer,
      designFileLink: item.designFileLink,
      printVendor: item.printVendor,
      costPerUnit: item.costPerUnit,
      status: item.status,
      dueDate: item.dueDate,
      notes: item.notes,
      eventId: item.eventId || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editingItem) {
      const updated = { ...editingItem, ...form };
      setItems(prev =>
        prev.map(it => it.id === editingItem.id ? updated : it)
      );
      // Fire-and-forget API sync (send API-schema fields)
      apiPut(planningPath(`/stationery/${editingItem.id}`), toApiPayload(updated)).catch(() => {});
    } else {
      const newItem: StationeryItem = { id: generateId(), ...form };
      setItems(prev => [...prev, newItem]);
      apiPost<{ id?: number | string; item?: { id?: number | string } }>(planningPath('/stationery'), toApiPayload(newItem))
        .then(res => {
          const dbId = String(res?.id ?? res?.item?.id ?? '');
          if (dbId && dbId !== newItem.id) {
            setItems(prev => prev.map(it => it.id === newItem.id ? { ...it, id: dbId } : it));
          }
        })
        .catch(() => {});
    }
    closeModal();
  };

  const handleArchive = (id: string) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, archived: true } : it));
    // Fire-and-forget API sync
    apiPut(planningPath(`/stationery/${id}`), { archived: true }).catch(() => {});
  };

  const handleRestore = (id: string) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, archived: false } : it));
    // Fire-and-forget API sync
    apiPut(planningPath(`/stationery/${id}`), { archived: false }).catch(() => {});
  };

  // Delete forever is only offered on an archived item; the list updates only
  // once the server confirms, and the dialog says why if it refuses.
  const forever = useDeleteConfirm<StationeryItem>({
    record: 'stationery item',
    perform: it => apiDelete(planningPath(`/stationery/${it.id}`)),
    onDone: it => { setItems(prev => prev.filter(x => x.id !== it.id)); closeModal(); },
  });
  const handlePermanentDelete = (id: string) => {
    const item = items.find(it => it.id === id);
    if (canDeleteForever(item)) forever.request(item!);
  };

  const updateStatus = (id: string, status: ItemStatus) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, status } : it));
    // Fire-and-forget API sync
    apiPut(planningPath(`/stationery/${id}`), { design_status: status }).catch(() => {});
  };

  // Items in the chosen view (active or archived) and category. The status
  // cards count THIS set, so a card keeps its number while it is the filter.
  const inView = items.filter(it => {
    if (showArchived !== (it.archived === true)) return false;
    return activeCategory === 'All' || it.category === activeCategory;
  });
  const filtered = inView.filter(it => filterStatus === 'All' || it.status === filterStatus);

  // Export button (Header): the rows this list shows right now.
  useExport('stationery', {
    rows: filtered,
    ctx: {
      categoryLabel: c => CATEGORY_LABELS[c as ItemCategory] ?? c,
      statusLabel: s => STATUS_LABELS[s as ItemStatus],
    },
  });

  const renderStatusCards = () => STATUS_CARDS.map(card => (
    <MetricCard
      key={card.status}
      label={card.label}
      value={inView.filter(i => i.status === card.status).length}
      tone={card.tone}
      icon={card.icon}
      onClick={() => toggleStatusFilter(card.status)}
      active={filterStatus === card.status}
      title={filterStatus === card.status ? `Showing ${card.label}. Click to clear.` : `Show only ${card.label}`}
    />
  ));

  const activeItems = items.filter(it => !it.archived);
  const archivedCount = items.filter(it => it.archived).length;
  const done = activeItems.filter(it => it.status === 'distributed' || it.status === 'printed').length;
  const inProgress = activeItems.filter(it =>
    it.status === 'design-in-progress' || it.status === 'design-approved' || it.status === 'sent-to-print'
  ).length;
  const pct = activeItems.length > 0 ? Math.round((done / activeItems.length) * 100) : 0;
  const totalCost = activeItems.reduce((sum, it) => sum + it.costPerUnit * it.quantityNeeded, 0);

  const categoryTabs = ['All', ...ITEM_CATEGORIES];

  const columns: Column<StationeryItem>[] = [
    idColumn<StationeryItem>('stationeryItem'),
    {
      key: 'category',
      header: 'Category',
      width: '120px',
      render: (_, it) => {
        const color = CATEGORY_COLORS[it.category] || '#475569';
        return (
          <span className="ds-badge ds-badge-dynamic" style={{ /* ds-dynamic: category color from map */ '--ds-badge-accent': color } as React.CSSProperties}>
            {CATEGORY_LABELS[it.category as ItemCategory] ?? it.category}
          </span>
        );
      },
    },
    {
      key: 'name',
      header: 'Item',
      render: (_, it) => (
        <div>
          <button
            type="button"
            className="stationery-item-link"
            onClick={e => {
              e.stopPropagation();
              if (onItemClick) {
                onItemClick(it);
              } else {
                openEdit(it);
              }
            }}
          >
            {it.name}
          </button>
          {it.designer && <div className="stationery-item-format">Designer: {it.designer}</div>}
          {it.notes && <div className="stationery-item-notes">{it.notes}</div>}
        </div>
      ),
    },
    {
      key: 'quantityNeeded',
      header: 'Qty',
      width: '90px',
      render: (_, it) => (
        <div style={ { fontSize: '0.8125rem' }}>
          <div style={ { fontWeight: 600 }}>{it.quantityNeeded}</div>
          <div style={ { color: '#6b7280', fontSize: '0.75rem' }}>{it.quantityPrinted} printed</div>
        </div>
      ),
    },
    {
      key: 'costPerUnit',
      header: 'Cost',
      width: '110px',
      render: (_, it) => (
        <div style={ { fontSize: '0.8125rem' }}>
          {it.costPerUnit > 0 && <div style={ { color: '#6b7280', fontSize: '0.75rem' }}>{money.format(it.costPerUnit)}/unit</div>}
          {it.costPerUnit > 0 && <div style={ { fontWeight: 600 }}>{money.format(it.costPerUnit * it.quantityNeeded)}</div>}
          {it.costPerUnit === 0 && <span style={ { color: '#9ca3af' }}>—</span>}
        </div>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      width: '100px',
      render: (_, it) => it.dueDate
        ? <span style={ { fontSize: '0.8125rem' }}>{it.dueDate}</span>
        : <span style={ { color: '#9ca3af' }}>—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '160px',
      render: (_, it) => (
        <span className={`ds-badge ${STATUS_BADGE_CLASS[it.archived ? 'inactive' : STATUS_BADGE[it.status]]}`}>
          {it.archived ? 'Archived' : STATUS_LABELS[it.status]}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (_, it) => (
        <div className="st-actions-cell">
          {it.archived ? (
            <>
              <button
                className="st-action-btn st-btn-restore"
                title="Restore"
                onClick={e => { e.stopPropagation(); handleRestore(it.id); }}
              >
                <Icon name="refresh" size={14} />
              </button>
              <button
                className="st-action-btn st-btn-delete"
                title="Delete forever"
                onClick={e => { e.stopPropagation(); handlePermanentDelete(it.id); }}
              >
                <Icon name="trash" size={14} />
              </button>
            </>
          ) : (
            <button
              className="st-action-btn st-btn-archive"
              title="Archive"
              onClick={e => { e.stopPropagation(); handleArchive(it.id); }}
            >
              <Icon name="trash" size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const totalCostFormatted = totalCost > 0
    ? money.format(totalCost)
    : '—';

  if (isLoading) {
    return <div className="wt-container fade-in"><div className="planning-loading">Loading stationery data…</div></div>;
  }
  if (loadError != null) {
    return <div className="wt-container fade-in"><LoadErrorBox what="stationery" error={loadError} onRetry={() => setLoadAttempt(n => n + 1)} /></div>;
  }

  return (
    <div className="wt-container fade-in">
        {/* Stats — hidden while the single-item form is open below: these
            count the whole collection, so they say nothing about the one
            record being edited and only push it further down the page. */}
        {!showModal && (
          viewMode === 'list' ? (
            <section className="stats-grid">{renderStatusCards()}</section>
          ) : (
            /* dynamic - exempt from guardrail: grid columns count depends on KANBAN_COLS array length */
            // eslint-disable-next-line react/forbid-component-props
            <section className="stats-grid stats-grid--kanban" style={ { gridTemplateColumns: `repeat(${KANBAN_COLS.length}, 1fr)` }}>{renderStatusCards()}</section>
          )
        )}

        {/* List view */}
        {viewMode === 'list' && (
          <>
            <DataTable
            pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
              recordList={{ kind: 'stationeryItem' }}
              data={filtered}
              columns={columns}
              hoverable
              compact
              emptyMessage="No items found."
            />
            {totalCost > 0 && (
              <div className="st-total-cost">
                <span>Total Estimated Cost</span>
                <strong>{totalCostFormatted}</strong>
              </div>
            )}
          </>
        )}

        {/* Kanban view */}
        {viewMode === 'kanban' && (
          /* dynamic - exempt from guardrail: grid columns count depends on KANBAN_COLS array length */
          // eslint-disable-next-line react/forbid-component-props
          <div className="kanban-board" style={ { gridTemplateColumns: `repeat(${KANBAN_COLS.length}, 1fr)` }}>
            {KANBAN_COLS.map(col => {
              const colItems = filtered.filter(it => it.status === col);
              return (
                <div
                  key={col}
                  className={`kanban-column${dragOverCol === col ? ' kanban-drag-over' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOverCol(col); }}
                  onDragLeave={() => setDragOverCol(null)}
                  onDrop={() => {
                    if (draggedItemId) { updateStatus(draggedItemId, col); setDraggedItemId(null); setDragOverCol(null); }
                  }}
                >
                  <div className="kanban-column-header">
                    <span className={`ds-badge ${STATUS_BADGE_CLASS[STATUS_BADGE[col]]}`}>
                      {STATUS_LABELS[col]}
                    </span>
                  </div>
                  <div className="kanban-cards">
                    {colItems.map(it => {
                      const actions = it.archived
                        ? [
                            { icon: 'edit', title: 'Edit', onClick: () => openEdit(it) },
                            { icon: 'refresh', title: 'Restore', onClick: () => handleRestore(it.id) },
                            { icon: 'trash', title: 'Delete forever', onClick: () => handlePermanentDelete(it.id) },
                          ]
                        : [
                            { icon: 'edit', title: 'Edit', onClick: () => openEdit(it) },
                            { icon: 'trash', title: 'Archive', onClick: () => handleArchive(it.id) },
                          ];
                      return (
                        <div
                          key={it.id}
                          className={`kanban-card${draggedItemId === it.id ? ' kanban-dragging' : ''}`}
                          draggable
                          onDragStart={() => setDraggedItemId(it.id)}
                          onDragEnd={() => { setDraggedItemId(null); setDragOverCol(null); }}
                        >
                          <StatusCard
                            category={CATEGORY_LABELS[it.category as ItemCategory] ?? it.category}
                            priority={getItemPriority(it)}
                            title={`${recordLabel('stationeryItem', it.id)} ${it.name}`}
                            metaLines={[
                              ...(it.designer ? [it.designer] : []),
                              ...(it.dueDate ? [`Due: ${it.dueDate}`] : []),
                            ]}
                            actions={actions}
                          />
                        </div>
                      );
                    })}
                    {colItems.length === 0 && (
                      <div className="st-empty-col">No items</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Add / Edit Inline Form */}
      {showModal && (
        <div className="inline-form-page">
          <div className="inline-form-header">
            <button className="btn-back" onClick={closeModal}>
              <Icon name="arrowLeft" size={16} /> Back
            </button>
            <div className="flex items-center gap-2">
              <h2>{editingItem ? 'Edit Stationery Item' : 'Add Stationery Item'}</h2>
              <button
                className="primary-btn"
                onClick={handleSave}
                disabled={!form.name.trim()}
              >
                {editingItem ? 'Save Changes' : 'Add Item'}
              </button>
            </div>
          </div>
          <div className="inline-form-page">
              {/* Row 1 */}
              <div className="st-form-row">
                <div className="st-form-group st-form-group--wide">
                  <label>Item Name <span className="form-required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    placeholder="e.g. Wedding Invitation Suite"
                    className="form-input form-input--sm"
                  />
                </div>
                <div className="st-form-group">
                  <label>Category</label>
                  <SearchableSelect
                    value={form.category}
                    onChange={value => setForm(prev => ({ ...prev, category: value as ItemCategory }))}
                    className="form-input form-input--sm"
                    options={ITEM_CATEGORIES.map(c => ({ value: c, label: CATEGORY_LABELS[c] }))}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="st-form-row">
                <div className="st-form-group">
                  <label>Quantity Needed</label>
                  <input
                    type="number"
                    name="quantityNeeded"
                    value={form.quantityNeeded}
                    onChange={handleNumberChange}
                    min={0}
                    className="form-input form-input--sm"
                  />
                </div>
                <div className="st-form-group">
                  <label>Quantity Printed</label>
                  <input
                    type="number"
                    name="quantityPrinted"
                    value={form.quantityPrinted}
                    onChange={handleNumberChange}
                    min={0}
                    className="form-input form-input--sm"
                  />
                </div>
                <div className="st-form-group">
                  <label>Cost per Unit ({money.unit})</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="costPerUnit"
                    value={form.costPerUnit > 0 ? form.costPerUnit.toLocaleString(getAppLocale()) : ''}
                    onChange={e => {
                      const num = Number(e.target.value.replace(/,/g, '')) || 0;
                      setForm(prev => ({ ...prev, costPerUnit: num }));
                    }}
                    min={0}
                    className="form-input form-input--sm"
                  />
                </div>
                <div className="st-form-group">
                  <label>Total Cost ({money.unit})</label>
                  <input
                    type="text"
                    readOnly
                    value={form.costPerUnit > 0 && form.quantityNeeded > 0
                      ? money.format(form.costPerUnit * form.quantityNeeded)
                      : '—'}
                    className="form-input form-input--sm form-input--readonly"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="st-form-row">
                <div className="st-form-group">
                  <label>Designer Assigned</label>
                  <input
                    type="text"
                    name="designer"
                    value={form.designer}
                    onChange={handleFormChange}
                    placeholder="Designer name"
                    className="form-input form-input--sm"
                    list="st-designer-list"
                    autoComplete="off"
                  />
                  {teamMembers.length > 0 && (
                    <datalist id="st-designer-list">
                      {teamMembers.map(m => (
                        <option key={m.id} value={m.name} />
                      ))}
                    </datalist>
                  )}
                </div>
                <div className="st-form-group">
                  <label>Print Vendor</label>
                  <input
                    type="text"
                    name="printVendor"
                    value={form.printVendor}
                    onChange={handleFormChange}
                    placeholder="Vendor name"
                    className="form-input form-input--sm"
                  />
                </div>
              </div>

              {/* Row 4 */}
              <div className="st-form-row">
                <div className="st-form-group st-form-group--wide">
                  <label>Design File Link</label>
                  <input
                    type="url"
                    name="designFileLink"
                    value={form.designFileLink}
                    onChange={handleFormChange}
                    placeholder="https://..."
                    className="form-input form-input--sm"
                  />
                </div>
                <div className="st-form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={form.dueDate}
                    onChange={handleFormChange}
                    className="form-input form-input--sm"
                  />
                </div>
              </div>

              {/* Row: Status + Event */}
              <div className="st-form-row">
                <div className="st-form-group">
                  <label>Status</label>
                  <SearchableSelect
                    value={form.status}
                    onChange={value => setForm(prev => ({ ...prev, status: value as ItemStatus }))}
                    className="form-input form-input--sm"
                    options={ITEM_STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] }))}
                  />
                </div>
                <div className="st-form-group">
                  <label>Linked Event</label>
                  <SearchableSelect
                    value={form.eventId || ''}
                    onChange={value => setForm(prev => ({ ...prev, eventId: value }))}
                    options={events.map(ev => ({ value: ev.id, label: ev.title }))}
                    placeholder="— no event —"
                    className="form-input form-input--sm"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="st-form-group">
                <RichTextEditor
                  label="Notes"
                  value={form.notes}
                  onChange={val => setForm(prev => ({ ...prev, notes: val }))}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>

            {editingItem && (
              <div className="flex gap-2 st-actions-row">
                {editingItem.archived ? (
                  <>
                    <button
                      className="secondary-btn st-btn-outline-restore"
                      onClick={() => { handleRestore(editingItem.id); closeModal(); }}
                    >
                      Restore
                    </button>
                    <button
                      className="secondary-btn st-btn-outline-delete"
                      onClick={() => handlePermanentDelete(editingItem.id)}
                    >
                      Delete forever
                    </button>
                  </>
                ) : (
                  <button
                    className="secondary-btn st-btn-outline-delete"
                    onClick={() => { handleArchive(editingItem.id); closeModal(); }}
                  >
                    Archive
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this item forever?"
        subject={forever.target?.name}
        description="This cannot be undone."
        consequences={['The item and its notes', 'Its design files']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

// ── API refresh & sync ──────────────────────────────────────────────────────

/** Convert a frontend StationeryItem to the API create/update payload. */
function toApiPayload(item: Partial<StationeryItem>) {
  return {
    ...(item.name !== undefined && { name: item.name }),
    ...(item.category !== undefined && { category: item.category }),
    ...(item.quantityNeeded !== undefined && { quantity: item.quantityNeeded }),
    ...(item.status !== undefined && { design_status: item.status }),
    ...(item.printVendor !== undefined && { vendor: item.printVendor }),
    ...(item.printVendorVendorId !== undefined && { vendor_vendor_id: item.printVendorVendorId || null }),
    ...(item.costPerUnit !== undefined && { cost: item.costPerUnit }),
    ...(item.dueDate !== undefined && { due_date: item.dueDate || null }),
    ...(item.notes !== undefined && { notes: item.notes || null }),
    ...(item.designer !== undefined && { assigned_to: item.designer || null }),
    ...(item.designerVendorId !== undefined && { assigned_to_vendor_id: item.designerVendorId || null }),
  };
}

/** Normalise a raw API row into the frontend StationeryItem shape. */
function normaliseApiItem(row: any): StationeryItem {
  // Derive a single combined status from design_status + print_status
  const ds: string = row.design_status ?? 'not-started';
  const ps: string = row.print_status ?? 'not-started';
  let status: ItemStatus = 'not-started';
  if (ps === 'distributed') status = 'distributed';
  else if (ps === 'printed' || ps === 'sent-to-print') status = ps as ItemStatus;
  else if (ds === 'design-approved') status = 'design-approved';
  else if (ds === 'design-in-progress') status = 'design-in-progress';
  else status = 'not-started';

  return {
    id: String(row.id),
    name: row.name ?? '',
    category: (row.category ?? 'invitation') as ItemCategory,
    quantityNeeded: row.quantity ?? 0,
    quantityPrinted: 0,
    designer: row.assigned_to ?? '',
    designerVendorId: row.assigned_to_vendor_id ?? undefined,
    designFileLink: '',
    printVendor: row.vendor ?? '',
    printVendorVendorId: row.vendor_vendor_id ?? undefined,
    costPerUnit: Number(row.cost ?? 0),
    status,
    dueDate: row.due_date ?? '',
    notes: row.notes ?? '',
    eventId: undefined,
    archived: false,
  };
}

/**
 * Fetches stationery items from the GuestGuru API, caches in memory.
 * Falls back silently to the existing cache when unreachable.
 */
// Stationery writes happen inline on each mutation (apiPost/apiPut/apiDelete per item),
// so there is nothing to bulk-push. This stub satisfies the usePlanningSync import.
export const syncStationeryToApi = async (): Promise<void> => { /* no-op */ };

export const refreshStationeryFromApi = async (): Promise<StationeryItem[]> => {
  try {
    const raw = await apiGet<{ items: any[] } | any[]>(planningPath('/stationery'));
    const rows: any[] = Array.isArray(raw) ? raw : (raw as any)?.items ?? [];
    if (rows.length > 0) {
      const normalised = rows.map(normaliseApiItem);
      stationeryCache = normalised;
      return normalised;
    }
    return loadItems();
  } catch {
    return loadItems();
  }
};

export default StationeryTracker;
